import { AnalyticsTrackingEvent } from './events.js';
import { BrandclubEventType } from './types.js';

const timeoutPromise = (ms, name, fn) => {
    return new Promise((resolve, reject) => {
        setTimeout(() => {
            reject(new Error(`${name} timed out after ${ms}ms`));
        }, ms);
        fn().then(resolve, reject);
    });
};
function mapAppEventToExternalCustomEvent(bcEventName, supportedEventsConfig) {
    var _a, _b, _c, _d, _e, _f, _g, _h;
    if (!supportedEventsConfig) {
        return undefined;
    }
    // Page View events
    if (((_a = supportedEventsConfig.pageView) === null || _a === void 0 ? void 0 : _a.enabled) &&
        ((bcEventName === null || bcEventName === void 0 ? void 0 : bcEventName.endsWith('page view')) || (bcEventName === null || bcEventName === void 0 ? void 0 : bcEventName.endsWith('bc_checkout_page_view')))) {
        return {
            eventName: supportedEventsConfig.pageView.mappedEventName,
            eventType: BrandclubEventType.PageView,
        };
    }
    // Navigate to Retailer events
    if (((_b = supportedEventsConfig.navigateToRetailer) === null || _b === void 0 ? void 0 : _b.enabled) &&
        (bcEventName === null || bcEventName === void 0 ? void 0 : bcEventName.endsWith('openRetailerProductPage'))) {
        return {
            eventName: supportedEventsConfig.navigateToRetailer.mappedEventName,
            eventType: BrandclubEventType.NavigateToRetailer,
        };
    }
    // Add to Cart events
    if (((_c = supportedEventsConfig.addToCart) === null || _c === void 0 ? void 0 : _c.enabled) &&
        (bcEventName === null || bcEventName === void 0 ? void 0 : bcEventName.endsWith(':addToCart'))) {
        return {
            eventName: supportedEventsConfig.addToCart.mappedEventName,
            eventType: BrandclubEventType.AddToCart,
        };
    }
    // View Cart / View Bag events
    if (((_d = supportedEventsConfig.viewCart) === null || _d === void 0 ? void 0 : _d.enabled) &&
        (bcEventName === AnalyticsTrackingEvent.DTC_OPEN_BUYBOX
            || bcEventName === AnalyticsTrackingEvent.VIEW_BAG
            || bcEventName === AnalyticsTrackingEvent.BC_CHECKOUT_OPEN_BAG_PAGE)) {
        return {
            eventName: supportedEventsConfig.viewCart.mappedEventName,
            eventType: BrandclubEventType.ViewCart,
        };
    }
    // Initiate Checkout events - // triggered before landing on the checkout page
    if (((_e = supportedEventsConfig.initiateCheckout) === null || _e === void 0 ? void 0 : _e.enabled) &&
        (bcEventName === AnalyticsTrackingEvent.INITIATE_CHECKOUT
            || bcEventName === AnalyticsTrackingEvent.BC_USER_REQUESTED_CHECKOUT)) {
        return {
            eventName: supportedEventsConfig.initiateCheckout.mappedEventName,
            eventType: BrandclubEventType.InitiateCheckout,
        };
    }
    // Order Confirmation events // confirm the order details before placing the order on the checkout page
    if (((_f = supportedEventsConfig.orderConfirmation) === null || _f === void 0 ? void 0 : _f.enabled) &&
        (bcEventName === AnalyticsTrackingEvent.CHECKOUT_INTENT_CREATED)) {
        return {
            eventName: supportedEventsConfig.orderConfirmation.mappedEventName,
            eventType: BrandclubEventType.OrderConfirmation,
        };
    }
    // Order Submitted events // triggered after the order is placed on the checkout page
    if (((_g = supportedEventsConfig.orderSubmitted) === null || _g === void 0 ? void 0 : _g.enabled) &&
        (bcEventName === AnalyticsTrackingEvent.CHECKOUT_ORDER_SUBMIT)) {
        return {
            eventName: supportedEventsConfig.orderSubmitted.mappedEventName,
            eventType: BrandclubEventType.OrderSubmitted,
        };
    }
    // Purchase Confirmed events // triggered on the purchase confirmation page
    if (((_h = supportedEventsConfig.purchaseConfirmed) === null || _h === void 0 ? void 0 : _h.enabled) &&
        (bcEventName === AnalyticsTrackingEvent.CHECKOUT_ORDER_SUCCESS)) {
        return {
            eventName: supportedEventsConfig.purchaseConfirmed.mappedEventName,
            eventType: BrandclubEventType.PurchaseConfirmed,
        };
    }
    return undefined;
}
function parseEventAndPayload(eventName, payload, supportedEventsConfig) {
    var _a, _b;
    const mappedEvent = mapAppEventToExternalCustomEvent(eventName, supportedEventsConfig);
    if (!(mappedEvent === null || mappedEvent === void 0 ? void 0 : mappedEvent.eventName) || !(mappedEvent === null || mappedEvent === void 0 ? void 0 : mappedEvent.eventType)) {
        return {
            eventName: undefined,
            eventPayload: {},
        };
    }
    const { eventName: externalEventName, eventType } = mappedEvent;
    const convertedPayload = JSON.parse(JSON.stringify({
        stacklineSku: (payload === null || payload === void 0 ? void 0 : payload.sku) || (payload === null || payload === void 0 ? void 0 : payload.stacklineSku),
        retailerId: payload === null || payload === void 0 ? void 0 : payload.retailerId,
        retailerSku: payload === null || payload === void 0 ? void 0 : payload.retailerSku,
        retailPrice: payload === null || payload === void 0 ? void 0 : payload.retailPrice,
        pageId: payload === null || payload === void 0 ? void 0 : payload.pageId,
        pageType: payload === null || payload === void 0 ? void 0 : payload.pageType,
        brandId: payload === null || payload === void 0 ? void 0 : payload.brandId,
        brandName: payload === null || payload === void 0 ? void 0 : payload.brandName,
        categoryId: payload === null || payload === void 0 ? void 0 : payload.categoryId,
        categoryName: payload === null || payload === void 0 ? void 0 : payload.categoryName,
        subCategoryId: payload === null || payload === void 0 ? void 0 : payload.subCategoryId,
        subCategoryName: payload === null || payload === void 0 ? void 0 : payload.subCategoryName,
        jl_id: payload === null || payload === void 0 ? void 0 : payload.jl_id,
        jl_c: payload === null || payload === void 0 ? void 0 : payload.jl_c,
        jl_m: payload === null || payload === void 0 ? void 0 : payload.jl_m,
        jl_s: payload === null || payload === void 0 ? void 0 : payload.jl_s,
        cartItems: (_a = payload === null || payload === void 0 ? void 0 : payload.cartItems) === null || _a === void 0 ? void 0 : _a.map((item) => (item && {
            retailerId: item.retailerId,
            retailerSku: item.retailerSku,
            stacklineSku: item.stacklineSku,
            brandId: item.brandId,
            title: item.title || item.productName,
            brandName: item.brandName,
            categoryId: item.categoryId,
            categoryName: item.categoryName,
            subCategoryId: item.subCategoryId,
            subCategoryName: item.subCategoryName,
            retailPrice: item.retailPrice,
            quantity: item.quantity,
        })).filter(Boolean),
        checkoutItems: (_b = payload === null || payload === void 0 ? void 0 : payload.checkoutItems) === null || _b === void 0 ? void 0 : _b.map((item) => (item && {
            retailerId: item.retailerId,
            retailerSku: item.retailerSku,
            stacklineSku: item.stacklineSku,
            brandId: item.brandId,
            title: item.title,
            brandName: item.brandName,
            categoryId: item.categoryId,
            categoryName: item.categoryName,
            subCategoryId: item.subCategoryId,
            subCategoryName: item.subCategoryName,
            retailPrice: item.retailPrice,
            quantity: item.quantity,
        })).filter(Boolean),
        checkoutIntentId: payload === null || payload === void 0 ? void 0 : payload.checkoutIntentId,
    }));
    return {
        eventName: externalEventName,
        eventType,
        eventPayload: convertedPayload,
    };
}

export { parseEventAndPayload, timeoutPromise };
//# sourceMappingURL=utils.js.map
